import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import SidebarShell from '@/components/wom/SidebarShell';
import TfsBrand from '@/components/tfs/TfsBrand';
import { hasCap, CAP, canViewMasterData, canViewPersonnel, canViewAssignments, isGlobalAdmin, WORKSPACES, FUTURE_APPS } from '@/lib/workspaces';
import CustomerManager from '@/components/operations/CustomerManager';
import CustomerReportBuilder from '@/components/operations/CustomerReportBuilder';
import LocationManager from '@/components/operations/LocationManager';
import PersonnelMaster from '@/components/admin/PersonnelMaster';import AssignmentManager from '@/components/admin/AssignmentManager';import PersonnelHub from '@/components/personnel/PersonnelHub';
import SessionManagement from '@/components/admin/SessionManagement';
import ProviderManager from '@/components/operations/ProviderManager';
import AdminAssets from '@/components/admin/AdminAssets';
import ReportsView from '@/components/admin/ReportsView';import SystemSettings from '@/components/admin/SystemSettings';
import ReconciliationView from '@/components/admin/ReconciliationView';
import { toast } from 'sonner';
import {
  LayoutDashboard, ShieldCheck, Users, Settings, Clock, ExternalLink,
  SlidersHorizontal, Save, History, Search, Check, Boxes, Building2, MapPin, ArrowRight, IdCard, Link2, FileBarChart,
  UserPlus, MoreVertical, Power, PowerOff, Trash2, Lock, ArrowLeft, X,
  KeyRound, Eye, Copy, ShieldAlert, Unlock, Truck, Handshake, Scale,
} from 'lucide-react';
import UserForm from '@/components/admin/UserForm';
import { MobileNavGrid } from '@/components/common/mobile/MobileNavGrid';
import { moduleIcon, moduleColor } from '@/lib/moduleIdentity';
import { useIsMobile } from '@/components/common/mobile/useIsMobile';
import { Avatar } from '@/components/admin/Avatar';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const PlaceholderCard = ({ icon: Icon, title, desc, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={testid}>
    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0E2A47]/[0.06] text-[#0E2A47]"><Icon size={22} /></div>
    <div className="mt-4 text-lg font-bold text-[#0E2A47]">{title}</div>
    <p className="mt-1.5 text-sm text-slate-500">{desc}</p>
    <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
      <Clock size={12} /> Arriving next phase
    </span>
  </div>
);

const CapChip = ({ label }) => (
  <span className="inline-flex items-center rounded-full bg-[#0E2A47]/[0.06] px-2.5 py-0.5 text-[11px] font-medium text-[#0E2A47]">{label}</span>
);

// Application dependency graph (mirrors backend core/capabilities.WORKSPACE_CHILD_CAPS):
// each workspace application and the capabilities that live inside it. Shared
// company-record DATA capabilities (personnel/customers/locations/assignments/reports)
// are deliberately NOT children of any single app — they are multi-home (Administration
// Hub OR Operations Hub) and are managed in their own "Company Records" section below.
const WS_CHILDREN = {
  'workspace.payroll': ['payroll.view', 'payroll.enter_assigned', 'payroll.prepare', 'payroll.approve', 'payroll.process', 'payroll.house_account.authorize'],
  'workspace.contract_budgets': ['contract_budgets.view', 'contract_budgets.manage', 'contract_budgets.approve', 'contract_budgets.report'],
  'workspace.tfs_ops': ['ops.customers.view', 'ops.locations.view', 'ops.providers.view', 'ops.personnel.view', 'ops.assignments.view', 'ops.budget.view', 'ops.reports.view', 'ops.assignments.create', 'ops.assignments.edit', 'ops.assignments.end', 'ops.assignments.reactivate', 'ops.assignments.approve', 'ops.all_locations', 'ops.reports.export', 'ops.service_prices.view', 'ops.budget.allocate'],
  'workspace.field': ['field.supervisor'],
  'workspace.rout_client': ['client.acting_on_behalf'],
};
// Shared company-record data permissions, grouped by functional area. Granted
// independently of any application toggle (an Operations Hub user can hold these
// without the Administration Hub application being enabled).
const SHARED_DATA_GROUPS = [
  { title: 'Personnel', caps: ['personnel.view', 'personnel.manage', 'personnel.reactivate'] },
  { title: 'Customers & Service Locations', caps: ['masterdata.view', 'masterdata.manage_customers', 'masterdata.manage_locations', 'masterdata.reactivate'] },
  { title: 'Assignments', caps: ['assignment.view', 'assignment.manage', 'assignment.reactivate'] },
  { title: 'Reporting', caps: ['reports.view', 'reports.personnel_full'] },
];
const SHARED_DATA_CAPS = SHARED_DATA_GROUPS.flatMap((g) => g.caps);
const CHILD_PARENT = Object.entries(WS_CHILDREN).reduce((m, [ws, kids]) => {
  kids.forEach((c) => { m[c] = ws; });
  return m;
}, {});

// Links a central User account to a Personnel record (personnel.user_id -> users.id).
// Keeps the app-access role on the User; the Personnel record supplies the photo and
// company job title. Never infers a link from a similar name — the operator picks it.
function LinkedPersonnelCard({ user, canManage, token, onChanged }) {
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const linked = user.linked_personnel_id;

  useEffect(() => {
    if (!picking) return;
    let active = true;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/personnel`, { ...authHeaders, params: { search: q, status_filter: 'active' } });
        if (active) setResults((r.data || []).slice(0, 25));
      } catch { if (active) setResults([]); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [picking, q, token]);

  const linkTo = async (pid) => {
    setBusy(true);
    try {
      await axios.put(`${API}/personnel/${pid}/user-link`, { user_id: user.id }, authHeaders);
      toast.success('Personnel record linked');
      setPicking(false); setQ(''); setResults([]);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not link personnel record');
    } finally { setBusy(false); }
  };

  const unlink = async () => {
    if (!linked) return;
    setBusy(true);
    try {
      await axios.put(`${API}/personnel/${linked}/user-link`, { user_id: null }, authHeaders);
      toast.success('Personnel record unlinked');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not unlink personnel record');
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="cap-linked-personnel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
          <IdCard size={14} /> Company Personnel Record
        </div>
        {canManage && (
          linked ? (
            <div className="flex items-center gap-2">
              <button onClick={() => { setPicking((p) => !p); setQ(''); }} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-50 disabled:opacity-50"
                data-testid="cap-personnel-change-btn"><Link2 size={13} /> Change</button>
              <button onClick={unlink} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                data-testid="cap-personnel-unlink-btn"><X size={13} /> Unlink</button>
            </div>
          ) : (
            <button onClick={() => { setPicking((p) => !p); setQ(''); }} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0E2A47]/90 disabled:opacity-50"
              data-testid="cap-personnel-link-btn"><Link2 size={13} /> Link Personnel</button>
          )
        )}
      </div>

      <div className="mt-3" data-testid="cap-linked-personnel-summary">
        {linked ? (
          <div className="flex items-center gap-3">
            <Avatar userId={user.id} hasPhoto={user.linked_personnel_photo} name={user.linked_personnel_name || user.name} size={40} />
            <div className="min-w-0">
              <div className="font-semibold text-slate-800 truncate">
                #{user.linked_personnel_number} · {user.linked_personnel_name}
              </div>
              <div className="text-xs text-slate-500 truncate">{user.linked_personnel_job_title || 'No company job title set'}</div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-500">Not linked to a Personnel record. Photo and company job title come from Personnel once linked.</div>
        )}
      </div>

      {picking && canManage && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="cap-personnel-picker">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search personnel by name or number…" value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="cap-personnel-search" autoFocus />
          </div>
          <div className="mt-2 max-h-64 overflow-auto divide-y divide-slate-100">
            {results.length === 0 && <div className="py-6 text-center text-xs text-slate-400">No matching personnel.</div>}
            {results.map((p) => {
              const takenByOther = p.has_login && p.user_id && p.user_id !== user.id;
              return (
                <button key={p.id} onClick={() => linkTo(p.id)} disabled={busy || p.id === linked}
                  className="flex w-full items-center gap-3 px-1 py-2 text-left hover:bg-slate-50 disabled:opacity-50"
                  data-testid={`cap-personnel-option-${p.id}`}>
                  <Avatar userId={p.id} hasPhoto={false} name={p.name} size={30} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">#{p.personnel_number} · {p.name}</div>
                    <div className="text-xs text-slate-400 truncate">
                      {p.primary_operational_role || 'No job title'}
                      {takenByOther && <span className="ml-2 text-amber-600">· already linked to {p.linked_user_email}</span>}
                      {p.id === linked && <span className="ml-2 text-[#5FA718]">· currently linked</span>}
                    </div>
                  </div>
                  {p.id === linked ? <Check size={15} className="text-[#5FA718]" /> : <Link2 size={15} className="text-slate-300" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function CapabilityEditor() {
  const { user, token, startImpersonation } = useContext(AuthContext);
  const navigate = useNavigate();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const isGlobalAdmin = user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS);
  const canRemove = user?.role === 'owner';                 // removal = Global Administrator only (backend enforced)
  const canImpersonate = user?.role === 'owner';            // View-as = Global Administrator only (backend enforced)
  const canResetPw = user?.role === 'owner';                // Password reset = Global Administrator (owner) only (backend enforced)
  const canUserMenu = canResetPw || canImpersonate || canRemove;
  const canAddUsers = ['owner', 'admin'].includes(user?.role) || hasCap(user, CAP.ADMIN_CONTROLS);

  const [catalog, setCatalog] = useState(null);
  const [users, setUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);   // full /admin/users records (for UserForm supervisor list + create)
  const [markets, setMarkets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);

  const [view, setView] = useState('list');           // 'list' | 'edit'
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState([]);
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState('');
  const [roleF, setRoleF] = useState('All');
  const [typeF, setTypeF] = useState('All');
  const [statusF, setStatusF] = useState('All');

  const [formOpen, setFormOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);   // {user, action:'deactivate'|'reactivate'}
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);     // user pending password reset
  const [resetResult, setResetResult] = useState(null);     // {user, password, expires_at}
  const [resetBusy, setResetBusy] = useState(false);
  const [impBusy, setImpBusy] = useState(null);             // id of user being viewed-as
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [privOpen, setPrivOpen] = useState(false);
  const listScroll = useRef(0);
  const [photoBump, setPhotoBump] = useState(0);
  const canManagePersonnel = user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, CAP.PERSONNEL_MANAGE);

  const load = useCallback(async () => {
    try {
      const [c, u] = await Promise.all([
        axios.get(`${API}/admin/capabilities/catalog`, authHeaders),
        axios.get(`${API}/admin/capabilities/users`, authHeaders),
      ]);
      setCatalog(c.data);
      setUsers(u.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load capabilities');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canAddUsers) return;
    axios.get(`${API}/admin/users`, authHeaders).then((r) => setAdminUsers(r.data || [])).catch(() => {});
    axios.get(`${API}/admin/markets`, authHeaders).then((r) => setMarkets(r.data || [])).catch(() => {});
    axios.get(`${API}/customers`, authHeaders).then((r) => setCustomers(r.data || [])).catch(() => {});
    axios.get(`${API}/service-locations`, authHeaders).then((r) => setLocations(r.data || [])).catch(() => {});
  }, [canAddUsers]);

  const selected = users.find((x) => x.id === selectedId) || null;
  const isSelf = selected && selected.id === user?.id;
  const dirty = selected && JSON.stringify([...draft].sort()) !== JSON.stringify([...(selected.capabilities || [])].sort());

  const openUser = async (u) => {
    listScroll.current = window.scrollY;
    setSelectedId(u.id);
    setDraft([...(u.capabilities || [])]);
    setView('edit');
    window.scrollTo(0, 0);
    try {
      const r = await axios.get(`${API}/admin/capabilities/audit?user_id=${u.id}&limit=8`, authHeaders);
      setAudit(r.data || []);
    } catch { setAudit([]); }
  };

  const backToList = (force = false) => {
    if (dirty && !force) { setDiscardOpen(true); return; }
    setDiscardOpen(false);
    setView('list');
    setSelectedId(null);
    setDraft([]);
    setAudit([]);
    setTimeout(() => window.scrollTo(0, listScroll.current || 0), 0);
  };

  const toggle = (capId) => setDraft((d) => {
    const has = d.includes(capId);
    if (has) {
      // Disabling an application also clears its dependent capabilities so no
      // orphaned (child-without-parent) grant can remain.
      if (WS_CHILDREN[capId]) {
        const kids = WS_CHILDREN[capId];
        const removed = d.filter((c) => kids.includes(c)).length;
        if (removed > 0) toast.info(`${removed} capability(ies) inside this application were also removed.`);
        return d.filter((c) => c !== capId && !kids.includes(c));
      }
      return d.filter((c) => c !== capId);
    }
    // Enabling a capability inside an application auto-enables the application.
    const parent = CHILD_PARENT[capId];
    const add = parent && !d.includes(parent) ? [capId, parent] : [capId];
    return [...new Set([...d, ...add])];
  });

  // Modules whose action capabilities require the matching workspace-access grant
  // to actually appear on the user's welcome screen. Used to warn before saving a
  // contradictory config (action caps ON while workspace access is OFF).
  const MODULE_DEPS = [
    { ws: 'workspace.payroll', label: 'Payroll', prefixes: ['payroll.'] },
    { ws: 'workspace.contract_budgets', label: 'Budgets', prefixes: ['contract_budgets.'] },
  ];
  const violationsFor = (caps) =>
    MODULE_DEPS.filter((m) => caps.some((c) => m.prefixes.some((p) => c.startsWith(p))) && !caps.includes(m.ws));

  const [depWarn, setDepWarn] = useState(null);   // { violations: [...] }

  const persist = async (caps) => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/capabilities/users/${selected.id}`, { capabilities: caps }, authHeaders);
      setUsers((list) => list.map((x) => (x.id === selected.id ? { ...x, capabilities: r.data.after, explicit: true } : x)));
      toast.success(`Capabilities saved for ${selected.name}`);
      setDepWarn(null);
      setView('list');
      setSelectedId(null);
      setDraft([]);
      setTimeout(() => window.scrollTo(0, listScroll.current || 0), 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (!selected || !dirty || saving) return;
    setSummaryOpen(true);   // plain-language summary before saving
  };

  const changeStatus = async (u, newStatus) => {
    try {
      await axios.patch(`${API}/admin/users/${u.id}/status`, { status: newStatus }, authHeaders);
      toast.success(`${u.name} ${newStatus === 'Active' ? 'reactivated' : 'deactivated'}`);
      setStatusTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change status');
      setStatusTarget(null);
    }
  };

  const doResetPassword = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      const r = await axios.patch(`${API}/admin/users/${resetTarget.id}/password`, { auto_generate: true }, authHeaders);
      setResetResult({ user: resetTarget, password: r.data.generated_password, expires_at: r.data.password_expires_at });
      setResetTarget(null);
      toast.success(`Temporary password set. ${resetTarget.name}'s active sessions were signed out.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reset password');
    } finally { setResetBusy(false); }
  };

  const doImpersonate = async (u) => {
    setImpBusy(u.id);
    try {
      const r = await axios.post(`${API}/admin/users/${u.id}/impersonate`, {}, authHeaders);
      startImpersonation(r.data.token, {
        target_id: u.id, target_name: u.name, target_email: u.email,
        admin_name: r.data.admin?.name, session_id: r.data.session_id,
      });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not start View as User');
      setImpBusy(null);
    }
  };

  const KNOWN_ROLE_LABELS = {
    Internal: ['Global Administrator', 'General Administrator', 'Operations Manager', 'Field/Ops Supervisor', 'Field Personnel'],
    External: ['Facilities Director', 'Location Requester'],
  };
  const userTypeOf = (u) => u.user_type || 'Internal';
  const roleOptions = (() => {
    const seen = typeF === 'All'
      ? users.map((u) => u.role_label)
      : [...(KNOWN_ROLE_LABELS[typeF] || []), ...users.filter((u) => userTypeOf(u) === typeF).map((u) => u.role_label)];
    return ['All', ...Array.from(new Set(seen.filter(Boolean)))];
  })();
  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    if (q && !(u.name || '').toLowerCase().includes(q) && !(u.email || '').toLowerCase().includes(q)) return false;
    if (typeF !== 'All' && userTypeOf(u) !== typeF) return false;
    if (roleF !== 'All' && u.role_label !== roleF) return false;
    if (statusF !== 'All' && (u.status || 'Active') !== statusF) return false;
    return true;
  });

  const CapRow = ({ item, globalOnly }) => {
    const checked = draft.includes(item.id);
    const disabled = !selected || isSelf || saving || (globalOnly && !isGlobalAdmin);
    return (
      <label data-testid={`cap-toggle-${item.id}`}
        className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${
          disabled ? 'cursor-not-allowed opacity-60 border-slate-200' : 'cursor-pointer border-slate-200 hover:border-[#0E2A47]/40'
        } ${checked ? 'bg-[#8FC93A]/10 border-[#8FC93A]/50' : 'bg-white'}`}>
        <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${checked ? 'bg-[#0E2A47] border-[#0E2A47] text-white' : 'border-slate-300 bg-white'}`}>
          {checked && <Check size={13} />}
        </span>
        <input type="checkbox" className="sr-only" checked={checked} disabled={disabled} onChange={() => toggle(item.id)} />
        <span className="text-sm font-medium text-[#0E2A47]">{item.label}</span>
        {globalOnly && <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">Global admin only</span>}
      </label>
    );
  };

  // ---- Single application registry (reuses the App Hub registry + WS_CHILDREN) ----
  const labelMap = {};
  (catalog?.workspaces || []).forEach((w) => { labelMap[w.id] = w.label; });
  (catalog?.functions || []).forEach((f) => { labelMap[f.id] = f.label; });
  const labelOf = (id) => labelMap[id] || id;
  const wsMeta = Object.fromEntries(WORKSPACES.map((w) => [w.cap, w]));
  const APP_ICON = { tfs_admin: ShieldCheck, tfs_ops: Boxes, field: IdCard };
  const ACTIVE_APPS = [
    { key: 'rout_admin', cap: 'workspace.rout_admin' },
    { key: 'tfs_crm', cap: 'app.tfs_crm' },
    { key: 'payroll', cap: 'workspace.payroll' },
    { key: 'contract_budgets', cap: 'workspace.contract_budgets' },
    { key: 'tfs_admin', cap: 'workspace.tfs_admin' },
    { key: 'tfs_ops', cap: 'workspace.tfs_ops' },
    { key: 'field', cap: 'workspace.field' },
    { key: 'rout_client', cap: 'workspace.rout_client' },
  ];
  // Only genuinely-unbuilt placeholders belong here. Apps that have since shipped a live
  // surface (Calendar + the Operations Hub modules) are no longer "coming" and are pruned
  // so Users & Capabilities never advertises an already-active feature as upcoming.
  const SHIPPED_APP_KEYS = new Set(['calendar', 'inspections', 'training', 'vehicles', 'inventory', 'pmp']);
  const COMING_APPS = FUTURE_APPS.filter((a) => a.placeholder && !SHIPPED_APP_KEYS.has(a.key));
  const SENSITIVE = {
    'payroll.approve': 'FINAL authorization of the complete payroll run — Owner only',
    'payroll.process': 'Marks a run entered in QuickBooks',
    'payroll.house_account.authorize': 'Preliminary review / pre-authorization of Account 1000 entries — not final approval',
    'reports.personnel_full': 'Exposes sensitive personnel detail (DOB, training, IDs)',
    'contract_budgets.approve': 'Final budget approval',
    'masterdata.reactivate': 'Reactivates inactive master records',
    'personnel.reactivate': 'Reactivates inactive personnel',
    'assignment.reactivate': 'Reactivates inactive assignments',
  };
  const OWNER_ONLY_CAP = 'payroll.approve';

  const AppAccessCard = ({ app }) => {
    const meta = wsMeta[app.cap] || {};
    const title = meta.title || labelOf(app.cap);
    const kids = WS_CHILDREN[app.cap] || [];
    const on = draft.includes(app.cap);
    const Icon = APP_ICON[app.key];
    const lock = !selected || isSelf || saving;
    return (
      <div data-testid={`app-card-${app.key}`} className={`rounded-2xl border p-4 transition-colors ${on ? 'border-[#8FC93A]/50 bg-[#8FC93A]/[0.05]' : 'border-slate-200 bg-white'}`}>
        <div className="flex items-start gap-3">
          {meta.img ? <img src={meta.img} alt="" className="h-9 w-9 rounded-lg object-contain" />
                    : (Icon ? <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0E2A47]/[0.06] text-[#0E2A47]"><Icon size={18} /></span> : null)}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-bold text-[#0E2A47]">{title}</span>
              <span className="rounded-full border border-[#8FC93A]/60 px-2 py-0.5 text-[10px] font-bold uppercase text-[#5FA718]">Active</span>
            </div>
            {meta.subtitle && <div className="text-xs text-slate-500">{meta.subtitle}</div>}
          </div>
          <label data-testid={`app-master-${app.key}`} className={`ml-auto inline-flex items-center ${lock ? 'opacity-50' : 'cursor-pointer'}`}>
            <input type="checkbox" className="sr-only" checked={on} disabled={lock} onChange={() => toggle(app.cap)} />
            <span className={`relative h-6 w-11 rounded-full transition-colors ${on ? 'bg-[#0E2A47]' : 'bg-slate-300'}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </span>
          </label>
        </div>
        {kids.length > 0 && (
          <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
            {kids.map((c) => {
              const checked = draft.includes(c);
              const ownerOnly = c === OWNER_ONLY_CAP;
              const childLock = lock || (ownerOnly && user?.role !== 'owner');
              return (
                <label key={c} data-testid={`cap-toggle-${c}`} className={`flex items-start gap-2.5 rounded-lg px-2 py-1.5 ${childLock ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50'}`}>
                  <input type="checkbox" className="sr-only" checked={checked} disabled={childLock} onChange={() => toggle(c)} />
                  <span className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded border ${checked ? 'bg-[#0E2A47] border-[#0E2A47] text-white' : 'border-slate-300 bg-white'}`}>{checked && <Check size={11} />}</span>
                  <span className="text-sm">
                    <span className="font-medium text-[#0E2A47]">{labelOf(c)}</span>
                    {ownerOnly && <span className="ml-2 rounded bg-[#0E2A47]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#0E2A47]">Owner only</span>}
                    {SENSITIVE[c] && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700">Sensitive</span>}
                    {SENSITIVE[c] && <span className="block text-[11px] text-slate-400">{SENSITIVE[c]}</span>}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const DataPermissionGroup = ({ group }) => {
    const lock = !selected || isSelf || saving;
    return (
      <div data-testid={`cap-data-group-${group.title.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
        className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0E2A47]/[0.06] text-[#0E2A47]"><IdCard size={16} /></span>
          <span className="font-bold text-[#0E2A47]">{group.title}</span>
        </div>
        <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3">
          {group.caps.map((c) => {
            const checked = draft.includes(c);
            return (
              <label key={c} data-testid={`cap-toggle-${c}`} className={`flex items-start gap-2.5 rounded-lg px-2 py-1.5 ${lock ? 'opacity-60' : 'cursor-pointer hover:bg-slate-50'}`}>
                <input type="checkbox" className="sr-only" checked={checked} disabled={lock} onChange={() => toggle(c)} />
                <span className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded border ${checked ? 'bg-[#0E2A47] border-[#0E2A47] text-white' : 'border-slate-300 bg-white'}`}>{checked && <Check size={11} />}</span>
                <span className="text-sm">
                  <span className="font-medium text-[#0E2A47]">{labelOf(c)}</span>
                  {SENSITIVE[c] && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700">Sensitive</span>}
                  {SENSITIVE[c] && <span className="block text-[11px] text-slate-400">{SENSITIVE[c]}</span>}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const ComingAppCard = ({ app }) => (
    <div data-testid={`app-card-${app.key}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-3">
        {app.img && <img src={app.img} alt="" className="h-8 w-8 rounded-lg object-contain opacity-80" />}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-600">{app.title}</span>
            <span className="rounded-full border border-slate-300 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-400">Coming</span>
          </div>
          <div className="text-[11px] text-slate-400">Access is managed once this application becomes Active.</div>
        </div>
      </div>
    </div>
  );

  const PrivilegedSection = () => {
    const isOwnerReq = user?.role === 'owner';
    const targetIsOwner = selected?.role === 'owner';
    const held = ['admin.controls', 'admin.user_access'].filter((c) => (selected?.capabilities || []).includes(c));
    return (
      <div data-testid="privileged-authority-section" className="mt-4 rounded-2xl border-2 border-amber-300 bg-amber-50/60 p-5">
        <div className="flex items-center gap-2 text-amber-800">
          <ShieldAlert size={18} /><span className="text-sm font-extrabold uppercase tracking-wide">Privileged System Authority</span>
        </div>
        <p className="mt-1 text-xs text-amber-700">These controls affect the entire TFS ecosystem and require elevated authorization.</p>
        {targetIsOwner ? (
          <div className="mt-3 rounded-xl border border-amber-200 bg-white px-4 py-3 text-sm" data-testid="owner-protected-card">
            <div className="font-bold text-[#0E2A47]">Owner Global Administrator</div>
            <div className="text-slate-500">{selected?.name} is the single, permanent Owner Global Administrator and cannot be demoted, locked, or altered here.</div>
          </div>
        ) : isOwnerReq ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="text-sm text-slate-600">Delegated authority held:{' '}
              <span className="font-semibold text-[#0E2A47]">{held.length ? held.map(labelOf).join(', ') : 'None'}</span>
            </div>
            <button onClick={() => setPrivOpen(true)} disabled={isSelf} data-testid="manage-privileged-btn"
              className="ml-auto inline-flex items-center gap-2 rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
              <Lock size={14} /> Manage Delegated Authority
            </button>
          </div>
        ) : (
          <div className="mt-3 rounded-xl border border-amber-200 bg-white px-4 py-3 text-sm text-slate-500" data-testid="privileged-locked">
            Only the Owner Global Administrator can view or change delegated global authority.
          </div>
        )}
      </div>
    );
  };

  const ActionButtons = ({ where }) => (
    <div className="flex items-center gap-2" data-testid={`cap-actions-${where}`}>
      <button onClick={() => backToList()} data-testid={`cap-cancel-${where}`}
        className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
        Cancel
      </button>
      <button onClick={save} disabled={!dirty || saving || isSelf} data-testid={`cap-save-${where}`}
        className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-colors ${
          !dirty || saving || isSelf ? 'bg-slate-300 cursor-not-allowed' : 'bg-[#0E2A47] hover:bg-[#16324F]'}`}>
        <Save size={15} /> {saving ? 'Saving…' : 'Save Changes'}
      </button>
    </div>
  );

  // ---------------------------------- LIST VIEW ----------------------------------
  if (view === 'list') {
    return (
      <div data-testid="capability-editor">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">TFS Administration Hub</div>
            <h1 className="mt-1.5 text-[30px] leading-tight font-extrabold tracking-tight text-[#0E2A47]">Users &amp; Capabilities</h1>
            <p className="mt-2 max-w-2xl text-base text-slate-500">
              Select a user to manage their workspace and action capabilities. Role sets the baseline; capabilities layer on top. Every change is audited.
            </p>
          </div>
          {canAddUsers && (
            <button onClick={() => setFormOpen(true)} data-testid="cap-add-user-btn"
              className="inline-flex items-center gap-2 rounded-xl bg-[#0E2A47] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#16324F]">
              <UserPlus size={16} /> Add User
            </button>
          )}
        </div>

        {user?.role === 'owner' && <SecurityAlerts authHeaders={authHeaders} />}

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search name or email…" value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="cap-user-search" />
          </div>
          <select value={typeF} onChange={(e) => { setTypeF(e.target.value); setRoleF('All'); }} data-testid="cap-type-filter"
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 bg-white">
            <option value="All">All user types</option>
            <option value="Internal">Internal — TFS User</option>
            <option value="External">External — Client User</option>
          </select>
          <select value={roleF} onChange={(e) => setRoleF(e.target.value)} data-testid="cap-role-filter"
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 bg-white">
            {roleOptions.map((r) => <option key={r} value={r}>{r === 'All' ? 'All roles' : r}</option>)}
          </select>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="cap-status-filter"
            className="rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 bg-white">
            {['All', 'Active', 'Inactive'].map((s) => <option key={s} value={s}>{s === 'All' ? 'All statuses' : s}</option>)}
          </select>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="cap-user-list">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">User</th>
                <th className="px-5 py-3 hidden md:table-cell">Role</th>
                <th className="px-5 py-3 hidden sm:table-cell">Type</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 hidden lg:table-cell">Capabilities</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`cap-user-row-${u.id}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <button onClick={() => openUser(u)} data-testid={`cap-edit-${u.id}`} className="flex items-center gap-3 text-left">
                      <Avatar userId={u.id} hasPhoto={u.has_photo ?? !!u.photo_url} name={u.name} size={34} />
                      <div className="min-w-0">
                        <div className="font-semibold text-[#0E2A47] hover:underline truncate">{u.name}</div>
                        <div className="text-xs text-slate-400 truncate">{u.email}</div>
                      </div>
                    </button>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell text-slate-600">{u.role_label}</td>
                  <td className="px-5 py-3 hidden sm:table-cell">
                    <span data-testid={`cap-user-type-${u.id}`}
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${userTypeOf(u) === 'External' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>
                      {userTypeOf(u) === 'External' ? 'External — Client' : 'Internal — TFS'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span data-testid={`cap-user-status-${u.id}`}
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${(u.status || 'Active') === 'Inactive' ? 'bg-slate-200 text-slate-500' : 'bg-green-100 text-green-700'}`}>
                      {u.status || 'Active'}
                    </span>
                  </td>
                  <td className="px-5 py-3 hidden lg:table-cell text-slate-500">{(u.capabilities || []).length}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openUser(u)} data-testid={`cap-row-edit-${u.id}`}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-50">
                        Edit Capabilities
                      </button>
                      {canUserMenu && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="rounded-md p-1.5 hover:bg-gray-100 text-slate-500" data-testid={`cap-user-actions-${u.id}`}>
                              <MoreVertical size={16} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            {canResetPw && (
                              <DropdownMenuItem onClick={() => setResetTarget(u)} data-testid={`cap-reset-pw-${u.id}`}>
                                <KeyRound size={14} className="mr-2" /> Reset Password
                              </DropdownMenuItem>
                            )}
                            {canImpersonate && (
                              <DropdownMenuItem
                                disabled={u.id === user?.id || u.role === 'owner' || (u.status || 'Active') === 'Inactive' || impBusy === u.id}
                                onClick={() => doImpersonate(u)} data-testid={`cap-view-as-${u.id}`}>
                                <Eye size={14} className="mr-2" /> {impBusy === u.id ? 'Opening…' : 'View as User'}
                              </DropdownMenuItem>
                            )}
                            {canRemove && (
                              <>
                                <DropdownMenuSeparator />
                                {(u.status || 'Active') === 'Inactive' ? (
                                  <DropdownMenuItem onClick={() => changeStatus(u, 'Active')} data-testid={`cap-activate-${u.id}`}>
                                    <Power size={14} className="mr-2" /> Reactivate
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem disabled={u.id === user?.id} onClick={() => setStatusTarget({ user: u, action: 'deactivate' })}
                                    className="text-amber-700" data-testid={`cap-deactivate-${u.id}`}>
                                    <PowerOff size={14} className="mr-2" /> Deactivate
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem disabled={u.id === user?.id} onClick={() => setPurgeTarget(u)}
                                  className="text-red-600" data-testid={`cap-purge-${u.id}`}>
                                  <Trash2 size={14} className="mr-2" /> Purge…
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-16 text-center text-slate-400" data-testid="cap-empty-state">
                  <Users size={30} className="mx-auto mb-2 text-slate-300" />
                  {typeF === 'External' ? 'No external client users yet.' : 'No users match.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {formOpen && (
          <UserForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={() => { load(); axios.get(`${API}/admin/users`, authHeaders).then((r) => setAdminUsers(r.data || [])).catch(() => {}); }}
            editUser={null} markets={markets} users={adminUsers} customers={customers} locations={locations} />
        )}
        {statusTarget && (
          <AlertDialog open onOpenChange={(o) => !o && setStatusTarget(null)}>
            <AlertDialogContent data-testid="cap-status-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Deactivate {statusTarget.user.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  The user will lose access immediately but all historical records are preserved. You can reactivate them later.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cap-status-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction className="bg-amber-600 hover:bg-amber-700" data-testid="cap-status-confirm"
                  onClick={(e) => { e.preventDefault(); changeStatus(statusTarget.user, 'Inactive'); }}>
                  Deactivate
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {purgeTarget && <CapPurgeDialog user={purgeTarget} onClose={() => setPurgeTarget(null)} onPurged={load} authHeaders={authHeaders} usersList={users} />}
        {resetTarget && (
          <AlertDialog open onOpenChange={(o) => !o && setResetTarget(null)}>
            <AlertDialogContent data-testid="cap-reset-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Reset password for {resetTarget.name}?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-slate-500">
                    <p>A secure temporary password will be generated. The user must change it at next login (expires in 72 hours).</p>
                    <p className="font-medium text-amber-700">This immediately signs the user out of all active sessions. The existing password is never shown or retrieved.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cap-reset-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction disabled={resetBusy} onClick={(e) => { e.preventDefault(); doResetPassword(); }}
                  className="bg-[#0E2A47] hover:bg-[#16324F]" data-testid="cap-reset-confirm">
                  {resetBusy ? 'Resetting…' : 'Generate Temporary Password'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {resetResult && (
          <AlertDialog open onOpenChange={(o) => !o && setResetResult(null)}>
            <AlertDialogContent data-testid="cap-reset-result-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Temporary password for {resetResult.user.name}</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3 text-sm text-slate-500">
                    <p>Share this one time. It is <strong>not stored in plain text</strong> and cannot be shown again.</p>
                    <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2">
                      <code className="flex-1 font-mono text-base text-[#0E2A47]" data-testid="cap-reset-password-value">{resetResult.password}</code>
                      <button onClick={() => { navigator.clipboard?.writeText(resetResult.password); toast.success('Copied'); }}
                        className="rounded-md p-1.5 text-slate-500 hover:bg-slate-200" data-testid="cap-reset-copy">
                        <Copy size={15} />
                      </button>
                    </div>
                    <p className="text-xs">Expires {resetResult.expires_at ? _fmtDateTime(resetResult.expires_at) : 'in 72 hours'}. The user must change it at next login.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction onClick={() => setResetResult(null)} data-testid="cap-reset-result-done">Done</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    );
  }

  // ---------------------------------- EDIT VIEW ----------------------------------
  return (
    <div data-testid="cap-edit-view">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => backToList()} data-testid="cap-back-btn"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#0E2A47]">
          <ArrowLeft size={16} /> Back to Users
        </button>
        <ActionButtons where="top" />
      </div>

      {selected && (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="cap-identity-card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-4">
                <Avatar userId={selected.id} hasPhoto={selected.has_photo ?? !!selected.photo_url} name={selected.name} size={56} bump={photoBump} />
                <div>
                  <div className="text-xl font-bold text-[#0E2A47]" data-testid="cap-edit-name">{selected.name}</div>
                  <div className="text-sm text-slate-500">{selected.email}</div>
                  {selected.linked_personnel_job_title && (
                    <div className="text-sm font-medium text-slate-600" data-testid="cap-edit-job-title">{selected.linked_personnel_job_title}</div>
                  )}
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    <span className="text-[#5FA718] font-medium" data-testid="cap-edit-role">{selected.role_label}</span>
                    <span className="text-slate-300">·</span>
                    <span data-testid="cap-edit-type" className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${userTypeOf(selected) === 'External' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>
                      {userTypeOf(selected) === 'External' ? 'External — Client User' : 'Internal — TFS User'}
                    </span>
                    <span className="text-slate-300">·</span>
                    <span data-testid="cap-edit-status" className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${(selected.status || 'Active') === 'Inactive' ? 'bg-slate-200 text-slate-500' : 'bg-green-100 text-green-700'}`}>
                      {selected.status || 'Active'}
                    </span>
                  </div>
                  {isSelf && <div className="mt-2 text-xs font-medium text-amber-700">You cannot edit your own capabilities.</div>}
                </div>
              </div>
            </div>

            {userTypeOf(selected) === 'External' ? (
              <div className="mt-6 rounded-2xl border border-purple-200 bg-purple-50/60 p-5" data-testid="cap-external-notice">
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-purple-800">
                  <ShieldCheck size={16} /> External Client Account
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  This is an external client user. Their access is governed by their assigned customer and
                  authorized locations in ROUT — not by internal TFS capabilities. Personnel, TEAM, payroll and
                  Operations access are never granted here. Manage their customer and location scope in{' '}
                  <button onClick={() => navigate('/rout-admin')} className="font-semibold text-purple-800 underline" data-testid="cap-goto-rout-assignments">ROUT Access &amp; Assignments</button>.
                </p>
              </div>
            ) : (
              <>
                <LinkedPersonnelCard
                  user={selected}
                  canManage={canManagePersonnel}
                  token={token}
                  onChanged={() => { setPhotoBump((b) => b + 1); load(); }}
                />

                <div className="mt-6 space-y-4" data-testid="cap-app-sections">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Applications</div>
                  <div className="grid gap-3 lg:grid-cols-2">
                    {ACTIVE_APPS.map((a) => <AppAccessCard key={a.key} app={a} />)}
                  </div>

                  <div className="pt-2 text-xs font-bold uppercase tracking-wider text-slate-400">Company Records — Data Permissions</div>
                  <p className="-mt-2 text-xs text-slate-500">
                    The Administration Hub owns these records; authorized Operations Hub users may be granted the same
                    permissions here <span className="font-semibold">without</span> enabling the Administration Hub application.
                  </p>
                  <div className="grid gap-3 lg:grid-cols-2" data-testid="cap-data-permissions">
                    {SHARED_DATA_GROUPS.map((g) => <DataPermissionGroup key={g.title} group={g} />)}
                  </div>

                  <div className="pt-2 text-xs font-bold uppercase tracking-wider text-slate-400">Company-Wide &amp; Operational Apps (Coming)</div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {COMING_APPS.map((a) => <ComingAppCard key={a.key} app={a} />)}
                  </div>
                  <PrivilegedSection />
                </div>
              </>
            )}

            {audit.length > 0 && (
              <div className="mt-6 border-t border-slate-100 pt-4">
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                  <History size={13} /> Recent changes
                </div>
                <ul className="space-y-1.5 text-xs text-slate-500" data-testid="cap-audit-list">
                  {audit.map((a) => (
                    <li key={a.id}>
                      <span className="font-medium text-[#0E2A47]">{a.changed_by_name || a.changed_by_email}</span>
                      {' '}changed {a.before.length}→{a.after.length} caps · {_fmtDateTime(a.changed_at)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center justify-end">
            <ActionButtons where="bottom" />
          </div>
        </>
      )}

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent data-testid="cap-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>You have unsaved capability changes. Leaving now will discard them.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cap-discard-keep">Keep editing</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="cap-discard-confirm"
              onClick={(e) => { e.preventDefault(); backToList(true); }}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {depWarn && (
        <AlertDialog open onOpenChange={(o) => !o && setDepWarn(null)}>
          <AlertDialogContent data-testid="cap-depwarn-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Enable workspace access?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm text-slate-600">
                  <p>
                    {selected?.name} has {depWarn.violations.map((v) => v.label).join(' and ')} action
                    {depWarn.violations.length > 1 ? ' capabilities' : ' capability'} selected, but
                    {' '}{depWarn.violations.map((v) => v.label).join(' / ')} <strong>workspace access is off</strong>.
                  </p>
                  <p className="font-medium text-amber-700">
                    Without workspace access, the {depWarn.violations.map((v) => v.label).join(' / ')} tile will not appear
                    on their welcome screen even though the capabilities remain assigned.
                  </p>
                  <p>Enable workspace access so the module is reachable, or save without it.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cap-depwarn-cancel">Cancel</AlertDialogCancel>
              <button onClick={() => persist(draft)} disabled={saving} data-testid="cap-depwarn-save-anyway"
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                Save without access
              </button>
              <AlertDialogAction data-testid="cap-depwarn-enable" className="bg-[#0E2A47] hover:bg-[#16324F]"
                onClick={(e) => { e.preventDefault(); const merged = [...new Set([...draft, ...depWarn.violations.map((v) => v.ws)])]; setDraft(merged); persist(merged); }}>
                Enable access &amp; Save
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {summaryOpen && (
        <AlertDialog open onOpenChange={(o) => !o && setSummaryOpen(false)}>
          <AlertDialogContent data-testid="cap-summary-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Review changes for {selected?.name}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm text-slate-600">
                  {(() => {
                    const orig = selected?.capabilities || [];
                    const added = draft.filter((c) => !orig.includes(c));
                    const removed = orig.filter((c) => !draft.includes(c));
                    return (<>
                      {added.length > 0 && <div data-testid="summary-added"><span className="font-semibold text-[#5FA718]">Will gain:</span> {added.map(labelOf).join(', ')}</div>}
                      {removed.length > 0 && <div data-testid="summary-removed"><span className="font-semibold text-red-600">Will lose:</span> {removed.map(labelOf).join(', ')}</div>}
                      {added.length === 0 && removed.length === 0 && <div>No effective changes.</div>}
                    </>);
                  })()}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cap-summary-cancel">Back</AlertDialogCancel>
              <AlertDialogAction data-testid="cap-summary-confirm" className="bg-[#0E2A47] hover:bg-[#16324F]"
                onClick={(e) => { e.preventDefault(); setSummaryOpen(false); persist(draft); }}>Confirm &amp; Save</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {privOpen && selected && (
        <PrivilegedDialog selected={selected} authHeaders={authHeaders} onClose={() => setPrivOpen(false)}
          onDone={() => { setPrivOpen(false); load(); }} />
      )}

    </div>
  );
}

const PrivilegedDialog = ({ selected, authHeaders, onClose, onDone }) => {
  const OPTIONS = [
    { id: 'admin.controls', label: 'Global Admin Controls' },
    { id: 'admin.user_access', label: 'User Access Administration' },
  ];
  const orig = OPTIONS.reduce((m, o) => ({ ...m, [o.id]: (selected.capabilities || []).includes(o.id) }), {});
  const [sel, setSel] = useState(orig);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const grant = OPTIONS.filter((o) => sel[o.id] && !orig[o.id]).map((o) => o.id);
  const revoke = OPTIONS.filter((o) => !sel[o.id] && orig[o.id]).map((o) => o.id);
  const changed = grant.length || revoke.length;
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/capabilities/privileged`, { user_id: selected.id, grant, revoke, reason, password }, authHeaders);
      toast.success('Privileged authority updated'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update privileged authority'); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="privileged-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert size={18} className="text-amber-600" /> Delegated Global Authority</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-slate-600">
              <p>Grant or revoke system-wide administrative authority for <strong>{selected.name}</strong>. This provides broad control across the entire TFS ecosystem.</p>
              {OPTIONS.map((o) => (
                <label key={o.id} className="flex items-center gap-2" data-testid={`priv-opt-${o.id}`}>
                  <input type="checkbox" checked={!!sel[o.id]} onChange={() => setSel((s) => ({ ...s, [o.id]: !s[o.id] }))} />
                  <span className="font-medium text-[#0E2A47]">{o.label}</span>
                </label>
              ))}
              <div><div className="text-xs font-semibold text-slate-600">Reason (audited)</div>
                <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="priv-reason" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Why is this being changed?" /></div>
              <div><div className="text-xs font-semibold text-slate-600">Re-enter your Owner password</div>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="priv-password" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Owner password" /></div>
              <p className="text-xs font-medium text-amber-700">This action is permanently audited and the user is notified.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="priv-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={!changed || !password || busy} data-testid="priv-confirm" className="bg-amber-600 hover:bg-amber-700"
            onClick={(e) => { e.preventDefault(); submit(); }}>{busy ? 'Saving…' : 'Confirm'}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const SecurityAlerts = ({ authHeaders }) => {
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => {
    axios.get(`${API}/admin/security/alerts?status_filter=open`, authHeaders)
      .then((r) => setAlerts(r.data || [])).catch(() => setAlerts([]));
  };
  useEffect(() => { load(); }, []);

  const unlock = async (a) => {
    setBusy(a.id);
    try {
      await axios.post(`${API}/admin/users/${a.target_user_id}/unlock`, {}, authHeaders);
      toast.success(`${a.target_name || a.target_email} unlocked`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not unlock');
    } finally { setBusy(null); }
  };

  if (!alerts.length) return null;
  return (
    <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="security-alerts">
      <div className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-800">
        <ShieldAlert size={16} /> Security alerts — account lockouts ({alerts.length})
      </div>
      <ul className="space-y-2">
        {alerts.map((a) => (
          <li key={a.id} data-testid={`security-alert-${a.target_user_id}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm">
            <div className="min-w-0">
              <div className="font-semibold text-[#0E2A47]">{a.target_name || a.target_email}</div>
              <div className="text-xs text-slate-500">
                {a.failed_attempts} failed attempts · {_fmtDateTime(a.occurred_at)} · IP {a.ip}
                {a.lock_active ? <span className="ml-1 font-semibold text-amber-700">· locked</span>
                               : <span className="ml-1 text-slate-400">· lock expired</span>}
                <span className="ml-1 text-slate-400">· email {a.email_status}</span>
              </div>
            </div>
            <button onClick={() => unlock(a)} disabled={busy === a.id} data-testid={`security-unlock-${a.target_user_id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0E2A47] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#16324F] disabled:opacity-50">
              <Unlock size={13} /> {busy === a.id ? 'Unlocking…' : 'Unlock'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CapPurgeDialog = ({ user, onClose, onPurged, authHeaders, usersList = [] }) => {
  const [deps, setDeps] = useState(null);
  const [directives, setDirectives] = useState({});   // {collection: {handling, reassign_to}}
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/users/${user.id}/dependencies/detail`, authHeaders)
      .then((r) => setDeps(r.data))
      .catch(() => setDeps({ has_dependencies: true, modules: [], total_dependencies: -1 }));
  }, []);

  const target = (user.email || user.name || '').trim();
  const matches = confirmText.trim().toLowerCase() === target.toLowerCase();
  const modules = deps?.modules || [];
  const reassignOptions = usersList.filter((u) => u.id !== user.id && (u.status || 'Active') !== 'Inactive');

  const setDirective = (coll, patch) =>
    setDirectives((d) => ({ ...d, [coll]: { ...(d[coll] || {}), ...patch } }));

  const moduleResolved = (m) => {
    const d = directives[m.collection];
    if (!d?.handling) return false;
    if (d.handling === 'reassign') return !!d.reassign_to;
    return true;
  };
  const allResolved = modules.every(moduleResolved);
  const canExecute = matches && reason.trim().length >= 3 && allResolved && !busy;

  const runPurge = async (dry) => {
    setBusy(true);
    try {
      const body = { dry_run: dry, directives, reason: reason.trim(), confirm_email: confirmText.trim() };
      const r = await axios.post(`${API}/admin/users/${user.id}/purge-controlled`, body, authHeaders);
      if (dry) { setPlan(r.data); }
      else { toast.success('User purged after dependency resolution'); onPurged(); onClose(); }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Purge failed'));
      if (d?.unresolved) setPlan({ unresolved: d.unresolved, will_delete_user: false });
    } finally { setBusy(false); }
  };

  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="cap-purge-dialog" className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShieldAlert size={18} className="text-red-600" /> Review &amp; purge {user.name}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="max-h-[60vh] space-y-3 overflow-y-auto text-sm text-slate-600">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                <div><strong>{user.name}</strong> · {user.email}</div>
                <div className="text-slate-400">ID {user.id} · {deps?.user_status || user.status || 'Active'} · {deps ? `${deps.total_dependencies} linked record(s)` : 'checking…'}</div>
              </div>

              {!deps && <span>Checking historical records…</span>}

              {deps && modules.length === 0 && (
                <div className="rounded-md border border-green-200 bg-green-50 p-3 text-green-700" data-testid="cap-purge-clear">
                  No linked records found. This account can be safely purged.
                </div>
              )}

              {deps && modules.length > 0 && (
                <div className="space-y-2" data-testid="cap-purge-modules">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Linked records — choose how to resolve each</p>
                  {modules.map((m) => (
                    <div key={m.collection} className="rounded-lg border border-slate-200 p-3" data-testid={`cap-purge-module-${m.collection}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-[#0E2A47]">{m.label} <span className="text-slate-400">({m.count})</span></div>
                        {m.openable && m.route && (
                          <a href={m.route} className="inline-flex items-center gap-1 text-xs font-medium text-[#5FA718] hover:underline" data-testid={`cap-purge-open-${m.collection}`}>
                            <ExternalLink size={12} /> Open module
                          </a>
                        )}
                      </div>
                      {m.records?.length > 0 && (
                        <ul className="mt-1 max-h-24 overflow-y-auto text-xs text-slate-400">
                          {m.records.slice(0, 6).map((rec) => (
                            <li key={rec.id} className="truncate">• {rec.title || rec.id}{rec.created_at ? ` — ${_fmtDate(rec.created_at)}` : ''}</li>
                          ))}
                          {m.count > 6 && <li>…and {m.count - 6} more</li>}
                        </ul>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <select value={directives[m.collection]?.handling || ''} onChange={(e) => setDirective(m.collection, { handling: e.target.value })}
                          data-testid={`cap-purge-handling-${m.collection}`}
                          className="rounded-md border border-slate-300 px-2 py-1.5 text-xs">
                          <option value="">Choose action…</option>
                          <option value="reassign">Reassign to another user</option>
                          {m.deletable && <option value="delete">Delete these records</option>}
                        </select>
                        {directives[m.collection]?.handling === 'reassign' && (
                          <select value={directives[m.collection]?.reassign_to || ''} onChange={(e) => setDirective(m.collection, { reassign_to: e.target.value })}
                            data-testid={`cap-purge-reassign-${m.collection}`}
                            className="rounded-md border border-slate-300 px-2 py-1.5 text-xs">
                            <option value="">Select user…</option>
                            {reassignOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {plan?.unresolved?.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="cap-purge-unresolved">
                  Unresolved: {plan.unresolved.map((u) => `${u.collection} (${u.reason})`).join('; ')}
                </div>
              )}

              <div className="space-y-2 pt-1">
                <div>
                  <div className="text-xs font-semibold text-slate-600">Reason (recorded in the audit log)</div>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cap-purge-reason"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Why is this user being purged?" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-600">Type <span className="font-mono text-[#0E2A47]">{target}</span> to confirm</div>
                  <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="cap-purge-input"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Enter email to confirm" />
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cap-purge-cancel">Cancel</AlertDialogCancel>
          <button onClick={() => runPurge(true)} disabled={busy} data-testid="cap-purge-preview"
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            Preview Impact
          </button>
          <AlertDialogAction disabled={!canExecute} onClick={(e) => { e.preventDefault(); runPurge(false); }}
            className="bg-red-600 hover:bg-red-700" data-testid="cap-purge-confirm">
            {busy ? 'Working…' : 'Execute Purge'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const ADMIN_MODULES = [
  { name: 'Client Communications', status: 'Live', view: 'customers' },
  { name: 'Calendar', status: 'Planned', route: null },
  { name: 'Inspections', status: 'Live', route: '/admin/cleaning-inspections' },
  { name: 'Preventive Maintenance', status: 'Pilot', route: '/admin/bank-pm' },
  { name: 'Building Intelligence', status: 'Pilot', route: '/admin/building-intelligence' },
  { name: 'Property Evaluations', status: 'In Development', route: '/admin/evaluations' },
  { name: 'Incident Reporting', status: 'Pilot', route: '/admin/incident-reports' },
  { name: 'Training', status: 'Planned', route: null },
  { name: 'Vehicle Inspections', status: 'Live', route: '/admin/inspections' },
  { name: 'Inventory', status: 'Planned', route: null },
  { name: 'Field Admin', status: 'Planned', route: null },
];

const STATUS_CLS = {
  Live: 'bg-[#8FC93A] text-[#0E2A47]', Pilot: 'bg-blue-500 text-white',
  'In Development': 'bg-amber-400 text-[#0E2A47]', Planned: 'bg-slate-300 text-slate-700',
};

function ModuleDirectory({ navigate, onOpenView }) {
  return (
    <div data-testid="module-directory">
      <div className="mb-6">
        <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">TFS Administration Hub</div>
        <h1 className="mt-1.5 text-[30px] leading-tight font-extrabold tracking-tight text-[#0E2A47]">Module Directory &amp; Standards</h1>
        <p className="mt-2 max-w-2xl text-base text-slate-500">
          Administrative configuration for the operational modules. Each module opens within the current
          ecosystem — you can always return here or to the App Hub. Work Orders are handled by ROUT.
        </p>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-5 py-3">Module</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Configuration</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {ADMIN_MODULES.map((m) => (
              <tr key={m.name} data-testid={`module-dir-${m.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <td className="px-5 py-3 font-medium text-[#0E2A47]">{m.name}</td>
                <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${STATUS_CLS[m.status]}`}>{m.status}</span></td>
                <td className="px-5 py-3 text-right">
                  {m.view ? (
                    <button onClick={() => onOpenView(m.view)}
                      data-testid={`module-configure-${m.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-50">
                      Configure <ArrowRight size={13} />
                    </button>
                  ) : m.route ? (
                    <button onClick={() => navigate(m.route)}
                      data-testid={`module-configure-${m.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-50">
                      Configure <ExternalLink size={13} />
                    </button>
                  ) : <span className="text-xs text-slate-400" data-testid={`module-unavailable-${m.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>Not Yet Available</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const MD_STAT_META = [
  { key: 'customers_active', label: 'Active Customers', pick: (d) => d.customers.active, tone: 'green' },
  { key: 'customers_inactive', label: 'Inactive Customers', pick: (d) => d.customers.inactive, tone: 'amber' },
  { key: 'locations_active', label: 'Active Service Locations', pick: (d) => d.locations.active, tone: 'green' },
  { key: 'locations_inactive', label: 'Inactive Service Locations', pick: (d) => d.locations.inactive, tone: 'amber' },
];

function CompanyMasterDataPanel({ onOpen, canCustomers, canLocations }) {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/master-data/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  return (
    <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 sm:mb-8 sm:p-6" data-testid="tfs-admin-masterdata-panel">
      <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0E2A47]/[0.06] text-[#0E2A47]"><ShieldCheck size={22} /></div>
        <div>
          <div className="text-lg font-bold text-[#0E2A47]">Company Master Data</div>
          <p className="text-sm text-slate-500">Authoritative customer &amp; service-location records — shared across ROUT and Operations.</p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {MD_STAT_META.map((s) => (
          <div key={s.key} data-testid={`md-stat-${s.key}`}
            className={`rounded-xl border p-3 sm:p-4 ${s.tone === 'green' ? 'border-[#8FC93A]/40 bg-[#8FC93A]/[0.07]' : 'border-amber-200 bg-amber-50'}`}>
            <div className="text-2xl font-extrabold tabular-nums text-[#0E2A47] sm:text-3xl" data-testid={`md-stat-value-${s.key}`}>
              {error ? '—' : (data ? s.pick(data) : '…')}
            </div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        {canCustomers && (
          <button onClick={() => onOpen('customers')} data-testid="md-open-customers"
            className="inline-flex items-center gap-2 rounded-xl bg-[#0E2A47] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#16324F]">
            <Building2 size={16} /> Manage Customers <ArrowRight size={15} />
          </button>
        )}
        {canLocations && (
          <button onClick={() => onOpen('locations')} data-testid="md-open-locations"
            className="inline-flex items-center gap-2 rounded-xl border border-[#0E2A47]/20 bg-white px-4 py-2.5 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50">
            <MapPin size={16} /> Manage Service Locations <ArrowRight size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

function PersonnelOverviewPanel({ onOpen }) {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    axios.get(`${API}/personnel/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);
  const stats = [
    { key: 'active', label: 'Active Personnel', tone: 'green' },
    { key: 'inactive', label: 'Inactive Personnel', tone: 'amber' },
    { key: 'w2', label: 'W-2 Employees', tone: 'green' },
    { key: 'contractor_1099', label: 'Individual 1099', tone: 'green' },
  ];
  return (
    <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 sm:mb-8 sm:p-6" data-testid="tfs-admin-personnel-panel">
      <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0E2A47]/[0.06] text-[#0E2A47]"><IdCard size={22} /></div>
          <div>
            <div className="text-lg font-bold text-[#0E2A47]">Personnel Master</div>
            <p className="text-sm text-slate-500">Authoritative directory of every individual who works for TFS.</p>
          </div>
        </div>
        <button onClick={() => onOpen('personnel')} data-testid="personnel-open"
          className="inline-flex items-center gap-2 rounded-xl bg-[#0E2A47] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#16324F]">
          Open Personnel <ArrowRight size={15} />
        </button>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.key} data-testid={`personnel-stat-${s.key}`}
            className={`rounded-xl border p-3 sm:p-4 ${s.tone === 'green' ? 'border-[#8FC93A]/40 bg-[#8FC93A]/[0.07]' : 'border-amber-200 bg-amber-50'}`}>
            <div className="text-2xl font-extrabold tabular-nums text-[#0E2A47] sm:text-3xl" data-testid={`personnel-stat-value-${s.key}`}>
              {error ? '—' : (data ? data[s.key] : '…')}
            </div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AssignmentsOverviewPanel({ onOpen }) {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    axios.get(`${API}/assignments/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);
  const stats = [
    { key: 'current', label: 'Current' }, { key: 'future', label: 'Future' },
    { key: 'expired', label: 'Expired' }, { key: 'inactive', label: 'Inactive' },
  ];
  return (
    <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 sm:mb-8 sm:p-6" data-testid="tfs-admin-assignments-panel">
      <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0E2A47]/[0.06] text-[#0E2A47]"><Link2 size={22} /></div>
          <div>
            <div className="text-lg font-bold text-[#0E2A47]">Personnel Assignments</div>
            <p className="text-sm text-slate-500">Effective-dated coverage across customers &amp; service locations.</p>
          </div>
        </div>
        <button onClick={() => onOpen('assignments')} data-testid="assignments-open"
          className="inline-flex items-center gap-2 rounded-xl bg-[#0E2A47] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#16324F]">
          Open Assignments <ArrowRight size={15} />
        </button>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.key} data-testid={`assignment-stat-${s.key}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
            <div className="text-2xl font-extrabold tabular-nums text-[#0E2A47] sm:text-3xl">{error ? '—' : (data ? (data[s.key] || 0) : '…')}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Standardized white sidebar branding panel (matches the other TFS app hubs):
// supplied TFS ADMIN1 icon centered on solid white, above the navy navigation.
const AdminBrand = () => (
  <div className="flex flex-col items-center border-b border-white/10 bg-white px-4 pt-5 pb-4 text-center" data-testid="admin-brand">
    <img src={moduleIcon('tfs_admin')} alt="TFS Administration Hub" className="w-[68%] max-w-[132px] h-auto object-contain" data-testid="admin-brand-icon" />
    <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#0E2A47]">Administration Hub</div>
  </div>
);


function AdminReportsArea() {
  const [tab, setTab] = React.useState('records');
  return (
    <div data-testid="admin-reports-area">
      <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-white p-1">
        <button onClick={() => setTab('records')} data-testid="reports-tab-records"
          className={`rounded-md px-4 py-1.5 text-sm font-semibold ${tab === 'records' ? 'bg-[#0E2A47] text-white' : 'text-slate-500 hover:text-[#0E2A47]'}`}>Records Report</button>
        <button onClick={() => setTab('customer')} data-testid="reports-tab-customer"
          className={`rounded-md px-4 py-1.5 text-sm font-semibold ${tab === 'customer' ? 'bg-[#0E2A47] text-white' : 'text-slate-500 hover:text-[#0E2A47]'}`}>Customer Report</button>
      </div>
      {tab === 'records' ? <ReportsView /> : <CustomerReportBuilder />}
    </div>
  );
}


function TfsAdminHub() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const canAdminUsers = user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS) || hasCap(user, 'admin.user_access');
  const canMasterData = canViewMasterData(user);
  const canPersonnel = canViewPersonnel(user);
  const canAssignments = canViewAssignments(user);
  const canReports = isGlobalAdmin(user) || hasCap(user, 'reports.view');
  const canReconcile = isGlobalAdmin(user);
  const canSettings = isGlobalAdmin(user) || hasCap(user, CAP.ADMIN_CONTROLS);
  // Module Directory & Standards is a development/governance area — Global Administrator only.
  const canModuleDirectory = user?.role === 'owner' || hasCap(user, CAP.ADMIN_CONTROLS);
  const canSessions = isGlobalAdmin(user);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab');
  const deepCustomerId = searchParams.get('customerId');
  const [view, setView] = useState(initialTab || 'overview');
  const isMobile = useIsMobile();

  const navItems = [
    { key: 'overview', label: 'Overview', icon: LayoutDashboard },
    ...(canMasterData ? [{ key: 'customers', label: 'Customers', icon: Building2 }] : []),
    ...(canMasterData ? [{ key: 'locations', label: 'Service Locations', icon: MapPin }] : []),
    ...(canMasterData ? [{ key: 'providers', label: 'Service Provider', icon: Handshake }] : []),
    ...(canPersonnel ? [{ key: 'personnel', label: 'Personnel', icon: IdCard }] : []),
    ...(canAssignments ? [{ key: 'assignments', label: 'Assignments', icon: Link2 }] : []),
    ...(canMasterData ? [{ key: 'assets', label: 'Assets', icon: Truck }] : []),
    ...(canReports ? [{ key: 'reports', label: 'Reports', icon: FileBarChart }] : []),
    ...(canReconcile ? [{ key: 'reconciliation', label: '2026 Reconciliation', icon: Scale }] : []),
    ...(canModuleDirectory ? [{ key: 'modules', label: 'Module Directory', icon: Boxes }] : []),
    ...(canAdminUsers ? [{ key: 'capabilities', label: 'Users & Capabilities', icon: SlidersHorizontal }] : []),
    ...(canSessions ? [{ key: 'sessions', label: 'Session Management', icon: ShieldCheck }] : []),
  ];

  const settingsGear = canSettings ? (
    <button onClick={() => setView('settings')} data-testid="sidebar-system-settings"
      aria-label="System Settings" title="System Settings"
      className={`group flex w-full items-center gap-3 rounded-lg px-2.5 py-2 transition-colors ${view === 'settings' ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`}>
      <Settings size={22} className="shrink-0" />
      <span className="text-sm font-semibold">System Settings</span>
    </button>
  ) : null;

  return (
    <SidebarShell
      navItems={navItems} activeKey={view} onNavigate={(k) => setView(k)}
      brandNode={<AdminBrand />} bottomSlot={settingsGear}
      mobileBrand={<img src={moduleIcon('tfs_admin')} alt="Admin Hub" className="h-8 w-8 object-contain" data-testid="admin-mobile-brand" />}
      workspaceLabel="TFS Administration Hub" logoutTo="/login" navAccent={moduleColor('tfs_admin')} showPortalHome
    >
      {/* Persistent mobile section nav — shown on EVERY admin screen with the current
          section highlighted (parent section stays active on detail/edit overlays). */}
      {isMobile && (
        <section className="mb-5" data-testid="admin-mobile-nav-wrap">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Administration</div>
          <MobileNavGrid
            accent={moduleColor('tfs_admin')}
            columns={3}
            testid="admin-mobile-nav"
            items={[
              ...navItems,
              ...(canSettings ? [{ key: 'settings', label: 'System Settings', icon: Settings }] : []),
            ].map((it) => ({ ...it, active: view === it.key, onClick: () => setView(it.key) }))}
          />
        </section>
      )}
      {view === 'capabilities' && canAdminUsers ? (
        <CapabilityEditor />
      ) : view === 'sessions' && canSessions ? (
        <div data-testid="tfs-admin-sessions"><SessionManagement /></div>
      ) : view === 'reports' && canReports ? (
        <div data-testid="tfs-admin-reports"><AdminReportsArea /></div>
      ) : view === 'reconciliation' && canReconcile ? (
        <div data-testid="tfs-admin-reconciliation"><ReconciliationView /></div>
      ) : view === 'settings' && canSettings ? (
        <div data-testid="tfs-admin-settings"><SystemSettings /></div>
      ) : view === 'customers' && canMasterData ? (
        <div data-testid="tfs-admin-customers"><CustomerManager openCustomerId={view === 'customers' ? deepCustomerId : null} /></div>
      ) : view === 'locations' && canMasterData ? (
        <div data-testid="tfs-admin-locations"><LocationManager /></div>
      ) : view === 'providers' && canMasterData ? (
        <div data-testid="tfs-admin-providers"><ProviderManager /></div>
      ) : view === 'personnel' && canPersonnel ? (
        <div data-testid="tfs-admin-personnel"><PersonnelHub embedded /></div>
      ) : view === 'assignments' && canAssignments ? (
        <div data-testid="tfs-admin-assignments"><AssignmentManager /></div>
      ) : view === 'assets' && canMasterData ? (
        <div data-testid="tfs-admin-assets"><AdminAssets canManage={['owner', 'admin', 'manager'].includes(user?.role)} /></div>
      ) : view === 'modules' && canModuleDirectory ? (
        <ModuleDirectory navigate={navigate} onOpenView={setView} />
      ) : (
        <>
          {/* Thin overview banner — supplied image, wheel kept fully visible on the
              right (object-contain right, no crop/stretch); title lives on the white
              left side. Long master-data explanation stays below the banner. */}
          <div className="mb-4 overflow-hidden rounded-2xl border border-slate-200 bg-white sm:mb-5" data-testid="admin-overview-banner">
            <div className="relative h-[88px] sm:h-[150px]">
              <img src="/module-banners/admin-banner.webp" alt="" aria-hidden="true"
                className="absolute right-0 top-0 h-full w-auto max-w-none object-contain object-right" data-testid="admin-banner-img" />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 32%, rgba(255,255,255,0.72) 48%, rgba(255,255,255,0) 68%)' }} aria-hidden="true" />
              <div className="relative z-10 flex h-full flex-col justify-center px-5 sm:px-6">
                <h1 className="text-[19px] sm:text-[32px] leading-tight font-extrabold tracking-tight text-[#0E2A47]" data-testid="admin-overview-title">Administration</h1>
              </div>
            </div>
          </div>

          {canMasterData && (
            <CompanyMasterDataPanel onOpen={setView} canCustomers={true} canLocations={true} />
          )}

          {canPersonnel && (
            <PersonnelOverviewPanel onOpen={setView} />
          )}

          {canAssignments && (
            <AssignmentsOverviewPanel onOpen={setView} />
          )}
        </>
      )}
    </SidebarShell>
  );
}

export default TfsAdminHub;
